import React from 'react';
import {
  Paper,
  Typography,
  Stack,
  useTheme,
  Link,
  IconButton,
  Box,
} from '@mui/material';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';

const ICON_SLOT_WIDTH = 20;

const IconSlot = ({ children }) => (
  <Box
    sx={{
      width: ICON_SLOT_WIDTH,
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    {children}
  </Box>
);

const splitValues = value =>
  (value || '')
    .split('//')
    .map(v => v.trim())
    .filter(Boolean);

const SubsidiaryCard = ({
  sucursal = {},
  onSelect,
  name,
  index,
  isSelected = false,
  onClose,
}) => {
  const theme = useTheme();

  if (!sucursal?.name) return null;

  const handleWhatsAppClick = (e, number) => {
    e.stopPropagation();
    const cleanNumber = number.replace(/\s+/g, '');
    if (cleanNumber) window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const handleCloseClick = e => {
    e.stopPropagation();
    onClose();
  };

  return (
    <Paper
      key={`paper-suc-${index}`}
      onClick={() => onSelect(sucursal)}
      elevation={0}
      sx={{
        position: 'relative',
        borderRadius: 2,
        cursor: 'pointer',
        border: isSelected
          ? `2px solid ${theme.primary}`
          : '2px solid transparent',
        backgroundColor: isSelected ? '#F6F6F6' : '#fff',
        boxShadow: onClose ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
        '&:hover': { backgroundColor: '#F6F6F6' },
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        color: '#000',
        '& .MuiTypography-root': { color: '#000' },
        pl: 2,
        py: 1.5,
        mb: 1,
      }}
    >
      {onClose && (
        <IconButton
          size="small"
          onClick={handleCloseClick}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <CloseIcon sx={{ fontSize: 18, color: 'black' }} />
        </IconButton>
      )}

      <Stack spacing={0.75}>
        {/* Nombre */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconSlot>
            <LocationOnIcon sx={{ fontSize: 18, color: theme.primary }} />
          </IconSlot>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
            {sucursal.name?.replace(name + '- ', '')}
          </Typography>
        </Stack>

        {/* Dirección */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconSlot />
          <Typography sx={{ fontSize: 15 }}>{sucursal.address}</Typography>
        </Stack>

        {/* Legajo */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconSlot />
          <Typography sx={{ fontSize: 15 }}>Leg. {sucursal.file}</Typography>
        </Stack>

        {/* Email */}
        {sucursal.email && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconSlot>
              <EmailIcon sx={{ fontSize: 18 }} />
            </IconSlot>
            <Link
              href={`mailto:${sucursal.email}`}
              underline="hover"
              sx={{
                fontSize: 15,
                color: 'blue !important',
                fontFamily: 'Open Sans,sans-serif',
                lineHeight: '16px',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={e => e.stopPropagation()}
            >
              {sucursal.email}
            </Link>
          </Stack>
        )}

        {/* Teléfono y WhatsApp en la misma línea, un número por renglón */}
        {(sucursal.whatsapp || sucursal.phone) && (
          <Stack direction="row" spacing={2}>
            {sucursal.whatsapp && (
              <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <IconSlot>
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#63cb77' }} />
                </IconSlot>
                <Stack sx={{ minWidth: 0 }}>
                  {splitValues(sucursal.whatsapp).map(number => (
                    <Typography
                      key={number}
                      onClick={e => handleWhatsAppClick(e, number)}
                      sx={{ fontSize: 15, cursor: 'pointer' }}
                    >
                      {number}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            )}

            {sucursal.phone && (
              <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <IconSlot>
                  <PhoneEnabledIcon sx={{ fontSize: 18 }} />
                </IconSlot>
                <Stack sx={{ minWidth: 0 }}>
                  {splitValues(sucursal.phone).map(number => (
                    <Typography key={number} sx={{ fontSize: 15 }}>
                      {number}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default SubsidiaryCard;
